import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(BarController, BarElement, CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

const getCssColor = (name, fallback) => {
  if (typeof document === "undefined") {
    return fallback;
  }

  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
};

const toRgba = (color, alpha) => {
  if (!color.startsWith("#")) {
    return color;
  }

  const value = color.replace("#", "");
  const fullValue = value.length === 3 ? value.split("").map((char) => char + char).join("") : value;
  const number = Number.parseInt(fullValue, 16);

  if (Number.isNaN(number)) {
    return color;
  }

  const red = (number >> 16) & 255;
  const green = (number >> 8) & 255;
  const blue = number & 255;

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

const UBIQUITI_COLORS = {
  blue: "#3b82f6",
  cyan: "#38bdf8",
  orange: "#f3a534",
  green: "#5ac878",
  red: "#ff4d55",
  violet: "#997adb",
  gray: "#d8dde5",
  cyanFill: "rgba(56, 189, 248, 0.1)",
  greenFill: "rgba(90, 200, 120, 0.08)",
  orangeFill: "rgba(243, 165, 52, 0.08)",
  violetFill: "rgba(153, 122, 219, 0.08)",
  grayFill: "rgba(216, 221, 229, 0.32)",
};

const DEFAULT_VISIBLE_SERIES = {
  download: true,
  upload: true,
  latency: true,
  packetLoss: true,
  totalAirtime: true,
  multicast: true,
  allowed: true,
  blocked: true,
};

const formatFrenchDecimal = (value, minimumFractionDigits = 1) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return null;
  }

  const plainValue = String(numericValue).includes("e")
    ? numericValue.toLocaleString("en-US", { maximumFractionDigits: 20, useGrouping: false })
    : String(numericValue);
  const [integerPart, fractionPart = ""] = plainValue.split(".");
  const sign = integerPart.startsWith("-") ? "-" : "";
  const absoluteInteger = integerPart.replace("-", "") || "0";
  const groupedInteger = Number(absoluteInteger).toLocaleString("fr-FR", { maximumFractionDigits: 0 });
  const visibleFraction = (fractionPart || "").padEnd(minimumFractionDigits, "0");

  return visibleFraction ? `${sign}${groupedInteger},${visibleFraction}` : `${sign}${groupedInteger}`;
};

const formatMetricValue = (value, unit) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return `${String(value).replace(".", ",")} ${unit}`;
  }

  const minimumFractionDigits = 1;
  const formattedValue = formatFrenchDecimal(numericValue, minimumFractionDigits);

  return unit === "%" ? `${formattedValue} %` : `${formattedValue} ${unit}`;
};

const formatAxisTick = (value) => formatFrenchDecimal(value, 1) || value;

const formatCompactAxisTick = (value) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return value;
  }

  if (Math.abs(numericValue) >= 1000) {
    const fractionDigits = Math.abs(numericValue) >= 10000 ? 0 : 1;
    const divisor = 10 ** fractionDigits;
    const compactValue = Math.round((numericValue / 1000) * divisor) / divisor;

    return `${formatFrenchDecimal(compactValue, fractionDigits)}K`;
  }

  return formatFrenchDecimal(numericValue, 0) || value;
};

const getTooltipLine = (label, value, unit) => {
  const formattedValue = formatMetricValue(value, unit);

  return formattedValue ? `${label} ${formattedValue}` : null;
};

const getTooltipDisplayLine = (label, displayValue, value, unit) => {
  if (displayValue !== null && displayValue !== undefined && displayValue !== "") {
    return `${label} ${String(displayValue).replace(".", ",")}`;
  }

  return getTooltipLine(label, value, unit);
};

const getTooltipDisplayCountLine = (label, displayValue, value) => {
  if (displayValue !== null && displayValue !== undefined && displayValue !== "") {
    return `${label} ${String(displayValue).replace(".", ",")}`;
  }

  const numericValue = Number(value);

  return Number.isFinite(numericValue) ? `${label} ${formatFrenchDecimal(numericValue, 0)}` : null;
};

const getDatasetColor = (dataset, fallback) => {
  const color = Array.isArray(dataset?.borderColor)
    ? dataset.borderColor[0]
    : dataset?.borderColor || dataset?.backgroundColor || fallback;

  return color === "transparent" ? dataset?.backgroundColor || fallback : color;
};

export default function UbiquitiInternetChart({ rows, theme, mode, visibleSeries = DEFAULT_VISIBLE_SERIES }) {
  const { t } = useTranslation();
  const isWifiMode = mode === "wifi";
  const isAirtimeMode = mode === "airtime";
  const isFlowsMode = mode === "flows";
  const hasIspMetrics = !isWifiMode && !isAirtimeMode && !isFlowsMode && rows.some((row) => row.usesIspMetrics);
  const wifiVisibleSeries = useMemo(() => ({
    activity: visibleSeries.activity ?? true,
    retries: visibleSeries.retries ?? true,
    clients: visibleSeries.clients ?? true,
  }), [visibleSeries.activity, visibleSeries.clients, visibleSeries.retries]);
  const airtimeVisibleSeries = useMemo(() => ({
    totalAirtime: visibleSeries.totalAirtime ?? true,
    multicast: visibleSeries.multicast ?? true,
  }), [visibleSeries.multicast, visibleSeries.totalAirtime]);
  const flowsVisibleSeries = useMemo(() => ({
    allowed: visibleSeries.allowed ?? true,
    blocked: visibleSeries.blocked ?? true,
  }), [visibleSeries.allowed, visibleSeries.blocked]);
  const wifiClientValues = rows.map((row) => Number(row.clients)).filter(Number.isFinite);
  const wifiRetryValues = rows.map((row) => Number(row.retries)).filter(Number.isFinite);
  const maxFlowValues = rows.map((row) => {
    const allowed = flowsVisibleSeries.allowed ? Number(row.allowed) : 0;
    const blocked = flowsVisibleSeries.blocked ? Number(row.blocked) : 0;

    return (Number.isFinite(allowed) ? allowed : 0) + (Number.isFinite(blocked) ? blocked : 0);
  }).filter(Number.isFinite);
  const flowsAxisMax = Math.ceil(Math.max(1, ...maxFlowValues) * 1.15);
  const maxWifiLeftMetric = Math.max(
    1,
    isWifiMode && wifiVisibleSeries.clients ? 60 : 0,
    isWifiMode && wifiVisibleSeries.retries ? 100 : 0,
    ...(isWifiMode && wifiVisibleSeries.clients ? wifiClientValues : []),
    ...(isWifiMode && wifiVisibleSeries.retries ? wifiRetryValues : []),
  );
  const wifiLeftAxisMax = wifiVisibleSeries.retries && maxWifiLeftMetric <= 100
    ? 100
    : Math.ceil(maxWifiLeftMetric * 1.2);
  const maxWifiTraffic = Math.max(1, ...rows.flatMap((row) => [Number(row.avgDownload), Number(row.avgUpload)]).filter(Number.isFinite));
  const primaryColor = useMemo(() => getCssColor("--primary-color", "#124188"), [theme]);
  const primaryFill = useMemo(() => toRgba(primaryColor, 0.1), [primaryColor]);
  const chart = useMemo(() => {
    const getDataValues = (key) => rows.map((row) => row[key] ?? null);
    const airtimeDatasets = [
      ...(airtimeVisibleSeries.totalAirtime ? [{
        label: "Total Airtime",
        metricKey: "totalAirtime",
        metricUnit: "%",
        data: rows.map((row) => row.totalAirtime ?? 0),
        borderColor: UBIQUITI_COLORS.orange,
        backgroundColor: UBIQUITI_COLORS.orangeFill,
        borderWidth: 1.5,
        pointRadius: rows.length === 1 ? 3 : 0,
        pointHoverRadius: 5,
        spanGaps: false,
        tension: 0.18,
        fill: true,
        order: 1,
        yAxisID: "y",
      }] : []),
      ...(airtimeVisibleSeries.multicast ? [{
        label: "Multicast",
        metricKey: "multicast",
        metricUnit: "%",
        data: rows.map((row) => row.multicast ?? 0),
        borderColor: UBIQUITI_COLORS.violet,
        backgroundColor: "transparent",
        borderDash: [2, 3],
        borderWidth: 1.5,
        pointRadius: rows.length === 1 ? 3 : 0,
        pointHoverRadius: 5,
        spanGaps: false,
        tension: 0.08,
        order: 2,
        yAxisID: "y",
      }] : []),
    ];
    const flowsDatasets = [
      ...(flowsVisibleSeries.allowed ? [{
        type: "bar",
        label: "Allowed",
        metricKey: "allowed",
        data: getDataValues("allowed"),
        borderColor: UBIQUITI_COLORS.blue,
        backgroundColor: UBIQUITI_COLORS.blue,
        borderWidth: 0,
        categoryPercentage: 1,
        barPercentage: 0.92,
        stack: "flows",
        order: 2,
        yAxisID: "y",
      }] : []),
      ...(flowsVisibleSeries.blocked ? [{
        type: "bar",
        label: "Blocked",
        metricKey: "blocked",
        data: getDataValues("blocked"),
        borderColor: UBIQUITI_COLORS.red,
        backgroundColor: UBIQUITI_COLORS.red,
        borderWidth: 0,
        categoryPercentage: 1,
        barPercentage: 0.92,
        stack: "flows",
        order: 1,
        yAxisID: "y",
      }] : []),
    ];
    const internetDatasets = hasIspMetrics ? [
      ...(visibleSeries.download ? [{
        label: t("Avg. Download"),
        metricKey: "avgDownload",
        metricUnit: "Mbps",
        data: getDataValues("avgDownload"),
        borderColor: UBIQUITI_COLORS.cyan,
        backgroundColor: "transparent",
        borderWidth: 1.5,
        pointRadius: rows.length === 1 ? 3 : 0,
        pointHoverRadius: 5,
        spanGaps: true,
        tension: 0.28,
        fill: false,
        yAxisID: "traffic",
      }] : []),
      ...(visibleSeries.upload ? [{
        label: t("Avg. Upload"),
        metricKey: "avgUpload",
        metricUnit: "Mbps",
        data: getDataValues("avgUpload"),
        borderColor: UBIQUITI_COLORS.violet,
        backgroundColor: "transparent",
        borderWidth: 1.5,
        pointRadius: rows.length === 1 ? 3 : 0,
        pointHoverRadius: 5,
        spanGaps: true,
        tension: 0.28,
        yAxisID: "traffic",
      }] : []),
      ...(visibleSeries.latency ? [{
        label: t("Avg. Latency"),
        metricKey: "avgLatency",
        metricUnit: "ms",
        data: getDataValues("avgLatency"),
        borderColor: UBIQUITI_COLORS.orange,
        borderDash: [2, 3],
        borderWidth: 1.5,
        pointRadius: rows.length === 1 ? 3 : 0,
        pointHoverRadius: 5,
        spanGaps: true,
        tension: 0.12,
        yAxisID: "latency",
      }] : []),
      ...(visibleSeries.packetLoss ? [{
        label: t("Packet loss"),
        metricKey: "packetLoss",
        metricUnit: "%",
        data: getDataValues("packetLoss"),
        borderColor: UBIQUITI_COLORS.red,
        backgroundColor: UBIQUITI_COLORS.red,
        borderDash: [2, 3],
        borderWidth: 1.5,
        pointRadius: rows.length === 1 ? 3 : 0,
        pointHoverRadius: 5,
        spanGaps: true,
        tension: 0,
        yAxisID: "latency",
      }] : []),
      {
        label: "",
        data: rows.map(() => 0),
        borderColor: "transparent",
        backgroundColor: "transparent",
        showLine: false,
        pointRadius: 0,
        pointHoverRadius: 0,
        pointHitRadius: 10,
        tooltipAnchor: true,
        yAxisID: "latency",
      },
    ] : [
      {
        label: t("Internet activity"),
        metricKey: "traffic",
        metricUnit: "Mbps",
        data: rows.map((row) => row.traffic),
        borderColor: primaryColor,
        backgroundColor: primaryFill,
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 5,
        tension: 0.35,
        fill: true,
        yAxisID: "y",
      },
      {
        label: t("Average latency"),
        metricKey: "latency",
        metricUnit: "ms",
        data: rows.map((row) => row.latency),
        borderColor: UBIQUITI_COLORS.violet,
        backgroundColor: UBIQUITI_COLORS.violetFill,
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 5,
        tension: 0.35,
        yAxisID: "y",
      },
      {
        label: t("Packet loss"),
        metricKey: "loss",
        metricUnit: "%",
        data: rows.map((row) => row.loss),
        borderColor: UBIQUITI_COLORS.red,
        borderDash: [3, 4],
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 5,
        tension: 0.2,
        yAxisID: "loss",
      },
    ];

    return {
      labels: rows.map((row) => row.label),
      datasets: isFlowsMode ? flowsDatasets : isAirtimeMode ? airtimeDatasets : isWifiMode ? [
        ...(wifiVisibleSeries.clients ? [{
          type: "bar",
          label: t("WiFi clients"),
          metricKey: "clients",
          metricUnit: t("clients unit"),
          data: rows.map((row) => row.clients),
          borderColor: "transparent",
          backgroundColor: UBIQUITI_COLORS.grayFill,
          borderWidth: 0,
          categoryPercentage: 1,
          barPercentage: 1,
          order: 4,
          yAxisID: "y",
        }] : []),
        ...(wifiVisibleSeries.activity ? [
          {
            label: t("Avg. Download"),
            metricKey: "avgDownload",
            metricUnit: "Mbps",
            data: getDataValues("avgDownload"),
            borderColor: UBIQUITI_COLORS.cyan,
            backgroundColor: "transparent",
            borderWidth: 1.4,
            pointRadius: rows.length === 1 ? 3 : 0,
            pointHoverRadius: 5,
            spanGaps: true,
            tension: 0.24,
            order: 1,
            yAxisID: "traffic",
          },
          {
            label: t("Avg. Upload"),
            metricKey: "avgUpload",
            metricUnit: "Mbps",
            data: getDataValues("avgUpload"),
            borderColor: UBIQUITI_COLORS.violet,
            backgroundColor: "transparent",
            borderWidth: 1.4,
            pointRadius: rows.length === 1 ? 3 : 0,
            pointHoverRadius: 5,
            spanGaps: true,
            tension: 0.24,
            order: 2,
            yAxisID: "traffic",
          },
        ] : []),
        ...(wifiVisibleSeries.retries ? [{
          label: t("Avg. TX Retries"),
          metricKey: "retries",
          metricUnit: "%",
          data: rows.map((row) => row.retries),
          borderColor: UBIQUITI_COLORS.orange,
          backgroundColor: "transparent",
          borderDash: [2, 3],
          borderWidth: 1.4,
          pointRadius: rows.length === 1 ? 3 : 0,
          pointHoverRadius: 5,
          spanGaps: true,
          tension: 0.1,
          order: 3,
          yAxisID: "y",
        }] : []),
        {
          label: "",
          data: rows.map(() => 0),
          borderColor: "transparent",
          backgroundColor: "transparent",
          showLine: false,
          pointRadius: 0,
          pointHoverRadius: 0,
          pointHitRadius: 10,
          tooltipAnchor: true,
          yAxisID: "y",
        },
      ] : internetDatasets,
    };
  }, [airtimeVisibleSeries, flowsVisibleSeries, hasIspMetrics, isAirtimeMode, isFlowsMode, isWifiMode, primaryColor, primaryFill, rows, t, visibleSeries, wifiVisibleSeries]);

  useEffect(() => {
    if (!hasIspMetrics) {
      return;
    }

    const datasetSummary = chart.datasets
      .filter((dataset) => !dataset.tooltipAnchor)
      .map((dataset) => {
        const values = dataset.data.filter((value) => Number.isFinite(value));

        return {
          label: dataset.label,
          axis: dataset.yAxisID,
          visiblePoints: values.length,
          firstValue: values[0] ?? null,
          lastValue: values[values.length - 1] ?? null,
          maxValue: values.length ? Math.max(...values) : null,
          minValue: values.length ? Math.min(...values) : null,
        };
      });

    window.__ubiquitiInternetChartDebug = {
      rows,
      labels: chart.labels,
      datasets: chart.datasets,
      datasetSummary,
      visibleSeries,
    };
  }, [chart, hasIspMetrics, rows, visibleSeries]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: rows.length > 600 ? false : { duration: 250 },
    interaction: {
      intersect: false,
      mode: "index",
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: theme === "dark" ? "#303030" : "#ffffff",
        titleColor: theme === "dark" ? "#ffffff" : "#11263c",
        bodyColor: theme === "dark" ? "#ffffff" : "#11263c",
        borderColor: theme === "dark" ? "rgba(255,255,255,.18)" : "rgba(17,38,60,.12)",
        borderWidth: 1,
        displayColors: true,
        padding: 10,
        filter: (context) => !context.dataset.tooltipAnchor && Number.isFinite(context.parsed?.y),
        callbacks: {
          title: (context) => {
            const row = rows[context?.[0]?.dataIndex];

            return row?.tooltipLabel || row?.label || "";
          },
          labelColor: (context) => {
            const color = getDatasetColor(context.dataset, primaryColor);

            return {
              borderColor: color,
              backgroundColor: color,
            };
          },
          label: (context) => {
            if (hasIspMetrics) {
              const row = rows[context.dataIndex];
              const metricKey = context.dataset.metricKey;

              if (metricKey === "avgDownload") {
                return [
                  getTooltipLine(t("Avg. Download"), row?.avgDownload, "Mbps"),
                  getTooltipLine(t("Max. Download"), row?.maxDownload, "Mbps"),
                ].filter(Boolean);
              }

              if (metricKey === "avgUpload") {
                return [
                  getTooltipLine(t("Avg. Upload"), row?.avgUpload, "Mbps"),
                  getTooltipLine(t("Max. Upload"), row?.maxUpload, "Mbps"),
                ].filter(Boolean);
              }

              if (metricKey === "avgLatency") {
                return [
                  getTooltipLine(t("Avg. Latency"), row?.avgLatency, "ms"),
                  getTooltipLine(t("Min. Latency"), row?.minLatency, "ms"),
                  getTooltipLine(t("Max. Latency"), row?.maxLatency, "ms"),
                ].filter(Boolean);
              }

              if (metricKey === "packetLoss") {
                return getTooltipLine(t("Packet loss"), row?.packetLoss, "%");
              }
            }

            if (isWifiMode) {
              const row = rows[context.dataIndex];
              const metricKey = context.dataset.metricKey;

              if (metricKey === "avgDownload") {
                return getTooltipDisplayLine(t("Avg. Download"), row?.avgDownloadDisplay, row?.avgDownload, "Mbps");
              }

              if (metricKey === "avgUpload") {
                return getTooltipDisplayLine(t("Avg. Upload"), row?.avgUploadDisplay, row?.avgUpload, "Mbps");
              }

              if (metricKey === "retries") {
                return getTooltipDisplayLine(t("Avg. TX Retries"), row?.retriesDisplay, row?.retries, "%");
              }

              if (metricKey === "clients") {
                return getTooltipDisplayLine(t("WiFi clients"), row?.clientsDisplay, row?.clients, t("clients unit"));
              }
            }

            if (isAirtimeMode) {
              const row = rows[context.dataIndex];
              const metricKey = context.dataset.metricKey;

              if (metricKey === "totalAirtime") {
                return [
                  getTooltipDisplayLine("Total Airtime", row?.totalAirtimeDisplay, row?.totalAirtime, "%"),
                  getTooltipDisplayLine("External Interference", row?.externalInterferenceDisplay, row?.externalInterference, "%"),
                  getTooltipDisplayLine("Internal Interference", row?.internalInterferenceDisplay, row?.internalInterference, "%"),
                ].filter(Boolean);
              }

              if (metricKey === "multicast") {
                return [
                  getTooltipDisplayLine("Multicast", row?.multicastDisplay, row?.multicast, "%"),
                  getTooltipDisplayLine("Tx Multicast", row?.txMulticastDisplay, row?.txMulticast, "%"),
                  getTooltipDisplayLine("Rx Multicast", row?.rxMulticastDisplay, row?.rxMulticast, "%"),
                  getTooltipDisplayCountLine("Rx Multicast Packets", row?.rxMulticastPacketsDisplay, row?.rxMulticastPackets),
                  getTooltipDisplayCountLine("Tx Multicast Packets", row?.txMulticastPacketsDisplay, row?.txMulticastPackets),
                ].filter(Boolean);
              }
            }

            if (isFlowsMode) {
              const row = rows[context.dataIndex];
              const metricKey = context.dataset.metricKey;

              if (metricKey === "allowed") {
                return getTooltipDisplayCountLine("Allowed", row?.allowedDisplay, row?.allowed);
              }

              if (metricKey === "blocked") {
                return getTooltipDisplayCountLine("Blocked", row?.blockedDisplay, row?.blocked);
              }
            }

            const formattedValue = formatMetricValue(context.raw, context.dataset.metricUnit || "");

            return formattedValue ? `${context.dataset.label} ${formattedValue}` : undefined;
          },
        },
      },
    },
    layout: {
      padding: {
        top: 10,
        right: 4,
        left: 4,
      },
    },
    scales: {
      x: {
        stacked: isFlowsMode,
        grid: {
          color: (context) => rows[context.index]?.label
            ? theme === "dark" ? "rgba(255,255,255,.08)" : "rgba(17,38,60,.08)"
            : "transparent",
        },
        ticks: {
          color: theme === "dark" ? "#d0d1d2" : "#697586",
          callback: (value, index) => rows[index]?.label || "",
          maxRotation: 0,
          autoSkip: false,
        },
        border: {
          display: false,
        },
      },
      y: {
        display: !hasIspMetrics,
        stacked: isFlowsMode,
        beginAtZero: true,
        suggestedMax: isFlowsMode ? flowsAxisMax : isAirtimeMode ? 100 : isWifiMode ? wifiLeftAxisMax : 130,
        grid: {
          color: theme === "dark" ? "rgba(255,255,255,.08)" : "rgba(17,38,60,.08)",
        },
        ticks: {
          color: theme === "dark" ? "#d0d1d2" : "#697586",
          maxTicksLimit: 5,
          callback: (value) => {
            const formattedValue = isFlowsMode ? formatCompactAxisTick(value) : formatAxisTick(value);

            return isAirtimeMode || (isWifiMode && wifiVisibleSeries.retries && !wifiVisibleSeries.clients)
              ? `${formattedValue}%`
              : formattedValue;
          },
        },
        border: {
          display: false,
        },
      },
      loss: {
        axis: "y",
        display: !hasIspMetrics && !isWifiMode && !isAirtimeMode && !isFlowsMode,
        position: "right",
        beginAtZero: true,
        suggestedMax: isWifiMode ? 25 : 12,
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          color: theme === "dark" ? "#d0d1d2" : "#697586",
          callback: (value) => `${formatAxisTick(value)}%`,
        },
        border: {
          display: false,
        },
      },
      latency: {
        axis: "y",
        display: hasIspMetrics,
        position: "left",
        beginAtZero: true,
        suggestedMax: 150,
        grid: {
          color: theme === "dark" ? "rgba(255,255,255,.08)" : "rgba(17,38,60,.08)",
        },
        ticks: {
          color: theme === "dark" ? "#d0d1d2" : "#697586",
          stepSize: 30,
          maxTicksLimit: 6,
          callback: formatAxisTick,
        },
        border: {
          display: false,
        },
      },
      traffic: {
        axis: "y",
        display: hasIspMetrics || isWifiMode,
        position: "right",
        beginAtZero: true,
        suggestedMax: isWifiMode ? Math.ceil(maxWifiTraffic * 1.15) : undefined,
        grace: "8%",
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          color: theme === "dark" ? "#d0d1d2" : "#697586",
          maxTicksLimit: 5,
          callback: formatAxisTick,
        },
        border: {
          display: false,
        },
      },
      packetLoss: {
        axis: "y",
        display: false,
        beginAtZero: true,
        suggestedMax: 5,
      },
      connectivity: {
        axis: "y",
        display: false,
        beginAtZero: true,
        suggestedMax: 100,
      },
      wifiPercent: {
        axis: "y",
        display: false,
        beginAtZero: true,
        suggestedMax: 25,
      },
    },
  };

  return (
    <div className="ubiquiti-chart-canvas">
      <Line data={chart} options={options} />
    </div>
  );
}
